import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button-v25/new';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import PaintBucketIcon from '@atlaskit/icon/core/paint-bucket';
import { JiraServiceManagementIcon, RovoIcon } from '@atlaskit/logo';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import { type TeamNavigationConfig } from '../jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from '../jsm-portal-playground-messages';

import { JsmPlaygroundSearchField } from './jsm-playground-search-field';

export type JsmPlaygroundNavigationProps = {
	config: TeamNavigationConfig;
	teamName: string;
	onCustomizeHelpCenterClick?: () => void;
};

const noop = () => {};

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconBefore']>;
type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const BackButtonIcon: IconButtonIcon = () => <ArrowLeftIcon label="" size="medium" />;
const CreateButtonIcon: ButtonIcon = () => <AddIcon label="" size="medium" />;
const CustomizeButtonIcon: ButtonIcon = () => <PaintBucketIcon label="" size="medium" />;
const AskRovoIcon: ButtonIcon = () => (
	<RovoIcon appearance="brand" label="" shouldUseHexLogo size="xxsmall" />
);

export const JsmPlaygroundNavigation = ({
	config,
	teamName,
	onCustomizeHelpCenterClick,
}: JsmPlaygroundNavigationProps) => {
	const intl = useIntl();

	return (
		<div css={navigationStyles}>
			<div css={navigationContentsStyles} data-testid="jsm-navigation-preview-contents">
				<div
					role="img"
					aria-label={intl.formatMessage(messages.navigationPreviewLabel, { teamName })}
					css={navigationChromeStyles}
				>
					<div
						aria-hidden="true"
						data-testid="jsm-navigation-preview-chrome"
						// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
						inert=""
						css={navigationChromeContentsStyles}
					>
						<div css={navigationStartStyles}>
							{config.showBack && (
								<IconButton
									appearance="subtle"
									icon={BackButtonIcon}
									label={intl.formatMessage(messages.back)}
									onClick={noop}
									spacing="compact"
									tabIndex={-1}
								/>
							)}

							<div css={productLockupStyles}>
								<JiraServiceManagementIcon
									appearance="brand"
									label=""
									shouldUseNewLogoDesign
									size="small"
								/>
								<Text color="color.text" maxLines={1} weight="bold">
									{intl.formatMessage(messages.productName)}
								</Text>
							</div>
						</div>

						{config.showSearch && (
							<div css={navigationMiddleStyles}>
								<JsmPlaygroundSearchField
									label={intl.formatMessage(messages.search)}
									placeholder={intl.formatMessage(messages.search)}
									tabIndex={-1}
								/>

								{config.showCreate && (
									<Button iconBefore={CreateButtonIcon} onClick={noop} tabIndex={-1}>
										{intl.formatMessage(messages.create)}
									</Button>
								)}
							</div>
						)}
					</div>
				</div>

				<div css={navigationEndStyles}>
					{config.showAskRovo && (
						<span
							aria-hidden="true"
							// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
							inert=""
						>
							<Button iconBefore={AskRovoIcon} onClick={noop} tabIndex={-1}>
								{intl.formatMessage(messages.askRovo)}
							</Button>
						</span>
					)}

					{config.showCustomizeHelpCenter && (
						<Button
							iconBefore={CustomizeButtonIcon}
							onClick={onCustomizeHelpCenterClick}
							testId="customize-help-center-button"
						>
							{intl.formatMessage(messages.customizeHelpCenter)}
						</Button>
					)}

					<span
						aria-hidden="true"
						css={[avatarStyles, config.avatarAppearance === 'outlined' && outlinedAvatarStyles]}
					>
						<Text
							color="color.text.inverse"
							size={config.avatarAppearance === 'outlined' ? 'large' : 'medium'}
							weight="semibold"
						>
							{config.avatarInitial}
						</Text>
					</span>
				</div>
			</div>
		</div>
	);
};

const navigationStyles = css({
	boxSizing: 'border-box',
	width: '100%',
	height: token('space.600'),
	flexShrink: 0,
	borderBlockEndWidth: token('border.width', '1px'),
	borderBlockEndStyle: 'solid',
	borderBlockEndColor: token('color.border'),
	backgroundColor: token('elevation.surface'),
	font: token('font.body'),
	userSelect: 'none',
});

const navigationContentsStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	paddingBlock: token('space.100'),
	paddingInline: token('space.150'),
});

const navigationChromeStyles = css({
	display: 'flex',
	flex: '1 1 auto',
	alignItems: 'center',
	minWidth: 0,
});

const navigationChromeContentsStyles = css({
	display: 'flex',
	flex: '1 1 auto',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
});

const navigationStartStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	minWidth: 0,
	flexShrink: 0,
});

const productLockupStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	minWidth: 0,
	paddingInline: token('space.050'),
	whiteSpace: 'nowrap',
});

const navigationMiddleStyles = css({
	display: 'flex',
	flex: '1 1 auto',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
	maxWidth: '610px',
	marginInline: 'auto',
});

const navigationEndStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	gap: token('space.050'),
	flexShrink: 0,
	marginInlineStart: 'auto',
});

const avatarStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	width: token('space.300'),
	height: token('space.300'),
	flexShrink: 0,
	borderRadius: token('radius.full', '9999px'),
	backgroundColor: token('color.background.accent.blue.bolder'),
});

const outlinedAvatarStyles = css({
	width: `calc(${token('space.300')} + ${token('space.050')})`,
	height: `calc(${token('space.300')} + ${token('space.050')})`,
	border: `${token('border.width.selected', '2px')} solid ${token('elevation.surface.overlay')}`,
});
